import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  WrenchScrewdriverIcon,
  BellAlertIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const TenantOnboardingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: WrenchScrewdriverIcon,
      title: 'Request Maintenance',
      description:
        'Submit maintenance requests directly to your landlord with photos and track their status in real-time.',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Message Your Landlord',
      description:
        'Communicate directly with your landlord through our secure messaging system. Keep all conversations in one place.',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      icon: BellAlertIcon,
      title: 'Get Notifications',
      description:
        'Receive important updates about your tenancy, maintenance requests, and messages from your landlord.',
      color: 'bg-red-50 text-red-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-6">
            <ShieldCheckIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Welcome to Your New Home! 🎉
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            You're all set! Manage your tenancy effortlessly with RentSmart.
            Here's everything you can do on our platform.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100"
            >
              <div
                className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4 ${feature.color}`}
              >
                <feature.icon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Quick Start Guide */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12 border border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Quick Start Guide
          </h2>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  Complete Your Profile
                </h4>
                <p className="text-gray-600">
                  Add your contact information and any other relevant details.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  Connect with Your Landlord
                </h4>
                <p className="text-gray-600">
                  Send a message to introduce yourself and ask any questions.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  Set Up Payment Tracking
                </h4>
                <p className="text-gray-600">
                  View your rent schedule and enable payment reminders.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate('/tenant-toolkit')}
            className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl w-full sm:w-auto"
          >
            Open Tenant Toolkit
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="px-8 py-4 bg-white text-blue-600 border-2 border-blue-600 rounded-xl font-semibold text-lg hover:bg-blue-50 transition-colors w-full sm:w-auto"
          >
            Complete Profile
          </button>
        </div>

        {/* Help Section */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-2">Need help getting started?</p>
          <button
            onClick={() => navigate('/help-center')}
            className="text-blue-600 hover:text-blue-700 font-semibold underline"
          >
            Visit our Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantOnboardingPage;
